import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import GenAIStrategySection from '@/components/solutions/GenAIStrategySection';

const RetailDataWorkshop = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Retail Data Workshop | Stratodata"
        description="A hands-on workshop to assess your data maturity, identify quick wins, and build a roadmap for your retail data transformation."
        canonical="/solutions/retail-data-workshop"
      />
      <Navigation />
      <main className="index-page px-7 md:px-10 flex-1">

        {/* Hero */}
        <section className="relative pt-24 pb-8 md:pt-28 md:pb-12 overflow-visible bg-white">
          <div
            className="relative mx-auto flex flex-col items-center justify-start text-center bg-no-repeat bg-center bg-contain min-h-[460px] md:min-h-[620px] px-6 pt-[110px] md:pt-[142px] pb-6"
            style={{
              backgroundImage: 'url(/lovable-uploads/cloud.png)',
              maxWidth: 'min(96vw, 1320px)',
              width: '100%',
            }}
          >
            <span className="inline-block px-4 py-2 rounded-lg text-white text-sm font-medium mb-5" style={{ backgroundColor: '#2A1E48' }}>
              Retail Management
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
              Retail Data Workshop
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-2">
              Clarity before complexity.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-2">
              Know exactly where your data stands — and where it needs to go.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-6">
              A structured engagement to assess, align and accelerate your retail data journey.
            </p>
            <Link
              to="/tell-me-more"
              className="inline-flex items-center justify-center px-8 py-3 rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#FF6B35]/90 transition-colors"
            >
              Contact us
            </Link>
          </div>
          <div className="relative z-10 flex justify-center -mt-[60px] md:-mt-[200px] px-0">
            <img
              src="/lovable-uploads/automations.png"
              alt="Retail Data Workshop"
              className="w-full max-w-3xl md:max-w-4xl h-auto object-contain drop-shadow-lg"
            />
          </div>
        </section>

        {/* Challenge */}
        <section id="challenge" className="pt-8 md:pt-12 pb-16 md:pb-24 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <span
                className="inline-block px-5 py-2.5 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: '#2A1E48' }}
              >
                Challenge
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              Why Retail Data Projects{' '}
              <span className="text-[#FF6B35]">Stall</span>
            </h2>
            <p className="text-foreground/90 text-center text-lg max-w-3xl mx-auto mb-12">
              Most retailers know they need better data. Few know where to start — or why their previous attempts didn't stick.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: 'No Shared Definition of Success',
                  text: 'Finance wants margin visibility. Marketing wants attribution. Operations wants stock KPIs. Without alignment on what "good data" means for the business, every data project becomes a political negotiation rather than a technical one.',
                },
                {
                  title: 'Investment Goes to the Wrong Place',
                  text: 'Retailers often jump straight to expensive BI tools or analytics platforms before the underlying data is reliable. The result: a beautiful dashboard built on dirty data — which leadership quickly stops trusting.',
                },
                {
                  title: 'No Map of What You Have',
                  text: 'Most organisations don\'t have a clear picture of what data sources exist, what quality they\'re in, or what\'s missing. Without this inventory, every initiative starts from scratch — and critical gaps only surface mid-project.',
                },
                {
                  title: 'Ownership Is Unclear',
                  text: 'Data projects fail not just technically but organisationally. When nobody owns the data model, data quality slips the moment the consultants leave. A successful data strategy requires clear ownership, processes and accountability inside the business.',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-xl p-6 text-left shadow-sm border border-orange-50/60 min-h-full"
                  style={{ background: 'linear-gradient(to bottom, #FFECD3 0%, #ffffff 100%)' }}
                >
                  <h3 className="font-bold text-foreground text-lg mb-3">{item.title}</h3>
                  <p className="text-foreground/90 text-sm leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Solution */}
        <section className="pt-0 pb-16 md:pb-24 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-6">
              <span
                className="inline-block px-5 py-2.5 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: '#2A1E48' }}
              >
                Solution
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              A Workshop Built for Retail
            </h2>
            <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-16">
              We run a structured, hands-on engagement with your team — assessing your current state, aligning on goals, and producing a concrete roadmap you can act on immediately.
            </p>

            {/* Block 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center mb-20">
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Data Maturity Assessment</h3>
                <p className="text-muted-foreground mb-4">
                  We start by mapping everything you have: data sources, pipelines, tools, team capabilities and existing reports. Nothing is assumed — everything is audited.
                </p>
                <p className="text-muted-foreground mb-4">
                  We score your current state across five dimensions — completeness, freshness, accuracy, accessibility and ownership — giving you a clear, honest picture of where you stand.
                </p>
                <p className="text-muted-foreground">
                  This becomes the baseline from which every recommendation is made. No generic advice — only decisions grounded in your actual situation.
                </p>
              </div>
              <div className="rounded-[24px] pt-5 pl-5 flex justify-end items-end w-full max-w-[460px] min-h-[275px]" style={{ backgroundColor: '#FFF7EC' }}>
                <div className="rounded-[20px] p-5 grid grid-cols-3 gap-6 w-full max-w-[400px] md:min-w-[400px] md:w-[400px] place-content-center place-items-center" style={{ backgroundColor: '#E0E7FF' }}>
                  {[
                    { file: 'ga4_icon.png', alt: 'GA4' },
                    { file: 'sap_icon.png', alt: 'SAP' },
                    { file: 'bq_icon.png', alt: 'BigQuery' },
                    { file: 'shopify_icon.png', alt: 'Shopify' },
                    { file: 'ads_icon.png', alt: 'Google Ads' },
                    { file: 'powerbi_icon.png', alt: 'Power BI' },
                    { file: 'dbt_icon.png', alt: 'dbt' },
                    { file: 'shoptet_icon.png', alt: 'Shoptet' },
                    { file: 'openai_icon.png', alt: 'OpenAI' },
                  ].map((logo) => (
                    <div key={logo.alt} className="rounded-full w-12 h-12 flex items-center justify-center bg-white shadow-sm flex-shrink-0 p-1.5">
                      <img
                        src={`/lovable-uploads/${logo.file}`}
                        alt={logo.alt}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Block 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center mb-20">
              <div className="order-2 md:order-1 rounded-[24px] pt-5 pr-5 pl-0 pb-0 flex justify-start items-end w-full max-w-[460px] min-h-[275px]" style={{ backgroundColor: '#FFF7EC' }}>
                <div className="flex-1 min-w-0 flex justify-start items-end">
                  <img
                    src="/lovable-uploads/strategic-north-star-tracking.png"
                    alt="Strategic roadmap and north star alignment"
                    className="w-full h-auto object-contain object-bottom rounded-2xl max-h-[245px]"
                  />
                </div>
              </div>
              <div className="order-1 md:order-2">
                <h3 className="text-2xl font-bold text-foreground mb-4">North Star Alignment</h3>
                <p className="text-muted-foreground mb-4">
                  Before any data model is designed, we facilitate alignment across your leadership team on the single most important business KPI — your North Star.
                </p>
                <p className="text-muted-foreground mb-4">
                  We challenge assumptions, surface conflicting priorities and drive consensus on what success looks like — so that every data decision that follows has a shared purpose behind it.
                </p>
                <p className="text-muted-foreground">
                  Teams that leave this session know exactly what they're optimising for. That clarity alone accelerates data projects by months.
                </p>
              </div>
            </div>

            {/* Block 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Concrete Roadmap & Quick Wins</h3>
                <ul className="space-y-3 text-muted-foreground">
                  {[
                    'Prioritised data roadmap with clear phases, owners and timelines.',
                    'Identification of quick wins that can be delivered within 30 days.',
                    'Architecture recommendation tailored to your current stack and budget.',
                    'Data ownership model — who is responsible for what, and how quality is maintained.',
                    'Executive-ready output so leadership can fund and support the next phase.',
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-[#FFECD3]" style={{ minWidth: 8, minHeight: 8 }} aria-hidden />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-[24px] pt-5 pl-5 pr-0 pb-0 flex justify-end items-end w-full max-w-[460px] min-h-[275px]" style={{ backgroundColor: '#FFF7EC' }}>
                <div className="flex-1 min-w-0 flex justify-end items-end">
                  <img
                    src="/lovable-uploads/automations_1.png"
                    alt="Data roadmap and implementation plan"
                    className="w-auto max-w-full h-auto object-contain object-bottom object-right rounded-2xl max-h-[245px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35] via-[#FF7F27] to-[#FF4500] rounded-3xl" />
          <div className="container-custom relative z-10">
            <div className="max-w-3xl mx-auto text-center pt-16 pb-8 px-4">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white leading-tight">
                Know Before You Build
              </h2>
              <p className="text-lg md:text-xl text-white mb-10 max-w-xl mx-auto">
                The Retail Data Workshop gives you the clarity, alignment and roadmap to make every future data investment count. Let's run one for your team.
              </p>
              <div className="flex justify-center">
                <Link
                  to="/tell-me-more"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-[#2a1e48] hover:bg-[#2a1e48]/90 text-white text-base font-medium transition-all duration-300 shadow-lg border border-white/10"
                >
                  Contact us
                </Link>
              </div>
            </div>
          </div>
        </section>

        <GenAIStrategySection />
      </main>
      <Footer />
    </div>
  );
};

export default RetailDataWorkshop;
